import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = 'admin_jwt';
const USER_KEY = 'admin_user';

const AuthContext = createContext(null);

/** Read token synchronously from localStorage (works before context mounts). */
export function readToken() {
  try { return localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

/** Axios interceptor: attach Bearer on every `/api/admin/*` call
 *  and force redirect on 401 (session expired / invalid). Installed
 *  exactly once at module load. */
let _installed = false;
function installAxiosInterceptors() {
  if (_installed) return;
  _installed = true;

  axios.interceptors.request.use((config) => {
    const t = readToken();
    if (!t) return config;
    const url = (config.url || '').toString();
    // Attach ONLY on admin endpoints (or when explicitly opted in).
    if (config.withAuth === true || /\/api\/admin\//.test(url)) {
      config.headers = config.headers || {};
      if (!config.headers.Authorization) config.headers.Authorization = `Bearer ${t}`;
    }
    return config;
  });

  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      const status = err?.response?.status;
      const url = (err?.config?.url || '').toString();
      if (status === 401 && /\/api\/admin\//.test(url)) {
        try {
          localStorage.removeItem(TOKEN_KEY);
          localStorage.removeItem(USER_KEY);
        } catch {}
        // Only redirect if we're already inside the admin surface — never
        // yank a public visitor to /admin/login.
        if (window.location.pathname.startsWith('/admin') && !window.location.pathname.startsWith('/admin/login') && !window.location.pathname.startsWith('/admin/invite')) {
          window.location.href = '/admin/login';
        }
      }
      return Promise.reject(err);
    },
  );
}

installAxiosInterceptors();

export function AuthProvider({ children }) {
  const navigate = useNavigate();
  const [user, setUser] = useState(() => {
    try {
      const raw = localStorage.getItem(USER_KEY);
      return raw ? JSON.parse(raw) : null;
    } catch { return null; }
  });
  const [checking, setChecking] = useState(true);

  // On mount, if we have a stored token, re-verify against /auth/me so the
  // session stays fresh (server may have disabled the account since login).
  useEffect(() => {
    const t = readToken();
    if (!t) { setChecking(false); return; }
    let cancelled = false;
    axios
      .get(`${API}/admin/auth/me`)
      .then((r) => { if (!cancelled) { setUser(r.data); try { localStorage.setItem(USER_KEY, JSON.stringify(r.data)); } catch {} } })
      .catch(() => { if (!cancelled) { setUser(null); try { localStorage.removeItem(TOKEN_KEY); localStorage.removeItem(USER_KEY); } catch {} } })
      .finally(() => { if (!cancelled) setChecking(false); });
    return () => { cancelled = true; };
  }, []);

  const login = useCallback(async (email, password) => {
    const { data } = await axios.post(`${API}/admin/auth/login`, { email, password });
    try {
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    } catch {}
    setUser(data.user);
    return data.user;
  }, []);

  const acceptInvite = useCallback(async (token, fullName, password) => {
    const { data } = await axios.post(`${API}/admin/auth/invite/${token}/accept`, {
      full_name: fullName,
      password,
    });
    try {
      localStorage.setItem(TOKEN_KEY, data.token);
      localStorage.setItem(USER_KEY, JSON.stringify(data.user));
    } catch {}
    setUser(data.user);
    return data.user;
  }, []);

  const logout = useCallback(() => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.removeItem(USER_KEY);
      localStorage.removeItem('admin_logged_in');
      localStorage.removeItem('admin_email');
    } catch {}
    setUser(null);
    navigate('/admin/login');
  }, [navigate]);

  const value = useMemo(() => ({ user, checking, login, logout, acceptInvite }), [user, checking, login, logout, acceptInvite]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const v = useContext(AuthContext);
  if (!v) throw new Error('useAuth must be used inside <AuthProvider>');
  return v;
}

// ─── Role gating ──────────────────────────────────────────────

export const ADMIN_ONLY_PATHS = [
  '/admin/dashboard',
  '/admin/exhibitions',
  '/admin/exhibition-flow',
  '/admin/tickets',
  '/admin/artworks',
  '/admin/sales',
  '/admin/invite',
  '/admin/users',
  '/admin/settings',
];

// Curator can access these (plus public shared routes below).
export const CURATOR_ALLOWED_PREFIXES = [
  '/admin/open-call',
  '/admin/artists',
  '/admin/email-console',
  '/admin/qr',
];

export function canRoleAccess(role, pathname) {
  if (!role) return false;
  if (role === 'admin') return true;
  // curator
  return CURATOR_ALLOWED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}
